import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Typography} from '@mui/material';
import {API_ENDPOINTS} from "../apiConfig";
import API from "../utils/API";
import Error from "./Error";
import Forms from "./Forms";
import {useTranslation} from 'react-i18next';
import RepairEditor from "./RepairEditor";
import CustomerModal from "../pages/admin/customer/CustomerModal";
import {useModal} from "../contexts/ModalContext";

const RepairModal = ({close, repair, onRepairUpdate, onCreated, initialBuybackId = null, initialInquiry = null}) => {
    const [repairs, setRepairs] = useState([]);
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const [deviceList, setDeviceList] = useState([]);
    const [dueDate, setDueDate] = useState('');
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customer, setCustomer] = useState(null);
    const [buybackOptions, setBuybackOptions] = useState([]);
    const [selectedBuyback, setSelectedBuyback] = useState(null);
    const [initialRepairs, setInitialRepairs] = useState([]);
    const {t} = useTranslation();
    const inputRef = useRef(null);
    const {openModal} = useModal();

    useEffect(() => {
        setTimeout(() => {
            inputRef.current?.focus?.();
        }, 0);
    }, []);

    useEffect(() => {
        if (!repair) return;
        if (repair.customer?._id) {
            setCustomer({
                value: repair.customer._id,
                label: repair.customer.type === 'person'
                    ? `${repair.customer.person?.name || ''} ${repair.customer.person?.surname || ''}`.trim()
                    : repair.customer.company?.name || 'Zákazník'
            });
        }
    }, [repair]);

    const buybackMap = useMemo(() => {
        return buybackOptions.reduce((acc, option) => {
            acc[option.value] = option;
            return acc;
        }, {});
    }, [buybackOptions]);
    const isBuybackMode = Boolean(selectedBuyback?.value);

    const fetchDevices = async () => {
        try {
            const response = await API.get(API_ENDPOINTS.device.list);
            setDeviceList(response.data || []);
        } catch (e) {
            console.error("Failed to fetch devices:", e);
        }
    };

    const loadCustomerList = async () => {
        try {
            const response = await API.get(API_ENDPOINTS.customer.list);
            const list = (response.data || []).map(customer => ({
                label: customer.type === 'person'
                    ? `${customer.person?.name || ''} ${customer.person?.surname || ''}`.trim()
                    : customer.company?.name,
                value: customer._id,
                data: customer
            }));
            setCustomerOptions(list);
        } catch (e) {
            console.error("Failed to fetch customers:", e);
        }
    };

    const loadBuybackList = async () => {
        try {
            const response = await API.get(API_ENDPOINTS.buybacks.list);
            const currentRepairId = repair?._id || null;
            const list = (response.data || [])
                .filter((buyback) => {
                    if (buyback?.intakePurpose === 'donor') {
                        return false;
                    }
                    const linkedRepair = buyback?.links?.repairId;
                    if (!linkedRepair) return true;

                    const linkedRepairId = typeof linkedRepair === 'string'
                        ? linkedRepair
                        : (linkedRepair._id || linkedRepair.value || null);

                    return Boolean(currentRepairId && linkedRepairId === currentRepairId);
                })
                .map((buyback) => {
                    const deviceName = buyback.deviceSnapshot?.name || buyback.device?.name || 'Neznámé zařízení';
                    const number = buyback.buybackNumber || buyback._id;
                    return {
                        label: `${number} • ${deviceName}`,
                        value: buyback._id,
                        data: buyback
                    };
                });
            setBuybackOptions(list);
        } catch (e) {
            console.error("Failed to fetch buybacks:", e);
        }
    };

    useEffect(() => {
        fetchDevices();
        loadCustomerList();
        loadBuybackList();
    }, []);

    const applyBuybackSelection = (option) => {
        setSelectedBuyback(option || null);
        if (!option?.data) {
            setInitialRepairs([]);
            return;
        }

        const buyback = option.data;
        const deviceId = buyback.device?._id || buyback.device;
        if (deviceId) {
            const device = deviceList.find(item => item._id === deviceId);
            setInitialRepairs([{
                device: {
                    value: deviceId,
                    label: `${device?.name || buyback.deviceSnapshot?.name || 'Zařízení'}${device?.code ? ` (${device.code})` : ''}`
                },
                repairList: []
            }]);
            setRepairs([{
                device: {
                    value: deviceId,
                    label: `${device?.name || buyback.deviceSnapshot?.name || 'Zařízení'}${device?.code ? ` (${device.code})` : ''}`
                },
                repairList: []
            }]);
        }

        const customerId = buyback.source?.customerId?._id || buyback.source?.customerId;
        if (customerId) {
            const customerOption = customerOptions.find(item => item.value === customerId);
            if (customerOption) {
                setCustomer(customerOption);
            } else {
                setCustomer({value: customerId, label: 'Zákazník z výkupu'});
            }
        } else {
            setCustomer(null);
        }
    };

    // Předvyplnění z požadavku z webu (rezervace / poptávka)
    const appliedInquiryRef = useRef(false);
    useEffect(() => {
        if (!initialInquiry || appliedInquiryRef.current || !deviceList.length) return;
        appliedInquiryRef.current = true;

        const inquiryCustomer = initialInquiry.customer;
        if (inquiryCustomer?._id) {
            setCustomer({
                value: inquiryCustomer._id,
                label: inquiryCustomer.type === 'company'
                    ? inquiryCustomer.company?.name || 'Zákazník'
                    : `${inquiryCustomer.person?.name || ''} ${inquiryCustomer.person?.surname || ''}`.trim() || 'Zákazník',
                data: inquiryCustomer
            });
        }

        const reservation = initialInquiry.reservation || {};
        // víc zařízení v reservation.devices, starší rezervace mají jedno zařízení v plochých polích
        const reservationDevices = reservation.devices?.length ? reservation.devices : [reservation];
        const rows = [];
        const unknownDevices = [];
        reservationDevices.forEach((item) => {
            const deviceId = item.device?._id || item.device;
            const device = deviceId ? deviceList.find(entry => entry._id === deviceId) : null;
            if (!device) {
                if (item.deviceName || item.modelCustom) unknownDevices.push(item.deviceName || item.modelCustom);
                return;
            }
            const repairList = (item.repairs || [])
                .map((repairItem) => {
                    const deviceRepair = (device.repairList || []).find(r =>
                        String(r._id) === String(repairItem.deviceRepair) || String(r.repair?._id) === String(repairItem.repairCatalog));
                    if (!deviceRepair?.repair) return null;
                    // cena, kterou zákazník viděl na webu
                    const price = repairItem.price ?? deviceRepair.price;
                    return {
                        label: `${deviceRepair.repair.name} (${price} Kč)`,
                        repairCatalogRepair: deviceRepair.repair._id,
                        deviceRepair: deviceRepair._id,
                        price
                    };
                })
                .filter(Boolean);
            rows.push({
                device: {value: device._id, label: `${device.name}${device.code ? ` (${device.code})` : ''}`},
                repairList
            });
        });
        if (rows.length) {
            setInitialRepairs(rows);
            setRepairs(rows);
        }

        const lines = [`Z požadavku z webu ${initialInquiry.number}.`];
        unknownDevices.forEach((name) => lines.push(`Zařízení: ${name}`));
        reservationDevices.forEach((item) => {
            if (!item.issue) return;
            const name = reservationDevices.length > 1 ? ` (${item.deviceName || item.modelCustom || item.familyName || 'zařízení'})` : '';
            lines.push(`Popis závady${name}: ${item.issue}`);
        });
        if (initialInquiry.type === 'business') {
            if (initialInquiry.business?.devices) lines.push(`Zařízení: ${initialInquiry.business.devices}`);
            if (initialInquiry.message) lines.push(initialInquiry.message);
        }
        if (initialInquiry.note) lines.push(`Poznámka zákazníka: ${initialInquiry.note}`);
        setNote(lines.join('\n'));
    }, [initialInquiry, deviceList]);

    useEffect(() => {
        if (!initialBuybackId || !buybackOptions.length) return;
        const option = buybackMap[initialBuybackId];
        if (option) {
            applyBuybackSelection(option);
        } else {
            setError({message: 'Z tohoto výkupu nelze založit opravu (donor nebo již navázán).'});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialBuybackId, buybackOptions.length, buybackMap]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError();
        try {
            const repairData = {
                customer: customer?.value || null,
                repairs: repairs.map(item => ({
                    device: item.device?.value,
                    repairList: item.repairList || []
                })),
                estimatedPrice: getEstimatedPrice(),
                dueDate,
                note: note ? {note} : undefined,
                buyback: selectedBuyback?.value || null
            };
            if (repair) {
                await API.put(API_ENDPOINTS.repairs.edit(repair._id), repairData);
            } else {
                const response = await API.post(API_ENDPOINTS.repairs.add, repairData);
                const repairId = response?.data?._id;
                if (repairId) {
                    await onCreated?.(response.data);
                }

                if (repairId && selectedBuyback?.value) {
                    await API.put(API_ENDPOINTS.buybacks.update(selectedBuyback.value), {
                        links: {
                            repairId
                        }
                    });
                }
            }
            onRepairUpdate?.();
            close?.();
        } catch (e) {
            setError(e);
        }
    };

    const getEstimatedPrice = () => {
        if (selectedBuyback?.value) {
            return 0;
        }
        return repairs.reduce((total, repairItem) => {
            const totalPerDevice = (repairItem.repairList || []).reduce((deviceTotalPrice, deviceRepair) => {
                if (deviceRepair.price) {
                    deviceTotalPrice += deviceRepair.price;
                }
                return deviceTotalPrice;
            }, 0);
            return total + totalPerDevice;
        }, 0);
    };

    const renderPrice = () => {
        let totalPrice = getEstimatedPrice();
        if (!totalPrice) return null;

        totalPrice = new Intl.NumberFormat('cs-CZ', {style: 'currency', currency: 'CZK'}).format(totalPrice);
        return <label className="text-green-600">{t('repairs.orderPrice', {price: totalPrice})}</label>;
    };

    const handleAddCustomer = (newCustomerData) => {
        const newCustomer = {
            label: newCustomerData.type === 'person'
                ? `${newCustomerData.person?.name || ''} ${newCustomerData.person?.surname || ''}`.trim()
                : newCustomerData.company?.name,
            value: newCustomerData._id,
            data: newCustomerData
        };
        setCustomerOptions(prev => [...prev, newCustomer]);
        setCustomer(newCustomer);
    };

    const openCustomerModal = () => {
        openModal(
            CustomerModal,
            {customer, onSaved: handleAddCustomer, strictPersonDuplicateByNameSurname: true},
            {title: customer ? t('customers.editCustomer') : t('customers.addCustomer')}
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 mb-4 items-end">
                <Forms.Select
                    placeholder="Výkup (volitelně)"
                    label="Výkup"
                    value={selectedBuyback}
                    options={buybackOptions}
                    onChange={applyBuybackSelection}
                />
                {selectedBuyback?.value && (
                    <Forms.Button
                        className="bg-gray-600 hover:bg-gray-700"
                        label="Odpojit výkup"
                        onClick={() => {
                            setSelectedBuyback(null);
                            setInitialRepairs([]);
                        }}
                    />
                )}
            </div>

            {!selectedBuyback?.value && (
                <div className="grid grid-cols-2 gap-4 mb-4 items-end">
                    <Forms.Select
                        ref={inputRef}
                        placeholder={t('repairs.customer')}
                        label={t('repairs.customer')}
                        value={customer}
                        options={customerOptions}
                        onChange={(val) => setCustomer(val)}
                        className="rounded-t-md"
                    />
                    <Forms.Button onClick={() => openCustomerModal()} label={t('repairs.addCustomer')}/>
                </div>
            )}

            {selectedBuyback?.value && (
                <div className="mb-4 text-sm text-gray-600">
                    Oprava je navázaná na výkup.
                </div>
            )}

            <div>
                <Typography variant="h6" className="flex justify-between">
                    <label>{t('repairs.repairList')}</label> {renderPrice()}
                </Typography>
                {deviceList && (
                    <RepairEditor
                        deviceList={deviceList}
                        initialRepairs={initialRepairs}
                        onRepairDataChange={setRepairs}
                        disableDeviceSelection={isBuybackMode}
                        hideAddRepairButton={isBuybackMode}
                        disableRemoveRepair={isBuybackMode}
                    />
                )}
            </div>

            <div className="mt-8">
                <h3>{t('repairs.orderInfo')}</h3>
                <div className="flex flex-col">
                    <Forms.Textarea
                        placeholder={t('note')}
                        label={t('note')}
                        value={note}
                        onChange={(val) => setNote(val)}
                        className="rounded-t-md"
                    />
                    <Forms.Date
                        label={t('repairs.dueDate')}
                        value={dueDate}
                        onChange={(val) => setDueDate(val)}
                    />
                </div>
            </div>

            {error && <Error error={error}/>}
            <Forms.Button submit label={repair ? t('update') : t('save')} className="mt-4"/>
        </form>
    );
};

export default RepairModal;
